import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  TrendingUp, 
  TrendingDown,
  BarChart3,
  Target,
  Clock,
  AlertTriangle,
  CheckCircle,
  Zap,
  Calendar,
  Info,
  RefreshCw,
  ChevronDown,
  ChevronRight,
  ArrowUpRight,
  ArrowDownRight,
  Download,
  FileText,
  FileSpreadsheet
} from 'lucide-react';
import { API } from '../App';
import { apiFetch } from '../services/api_service';


const PredictiveAnalytics = () => {
  const navigate = useNavigate();
  const [predictions, setPredictions] = useState(null);
  const [scenarios, setScenarios] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedMetric, setSelectedMetric] = useState('emissions');
  const [horizonMonths, setHorizonMonths] = useState(12);
  const [exporting, setExporting] = useState(false);

  const handleExport = async (format) => {
    setExporting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API}/predictions/export/${format}?horizon_months=${horizonMonths}`,
        { headers: token ? { 'Authorization': `Bearer ${token}` } : {} }
      );
      
      if (!response.ok) throw new Error('Export failed');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `esg_predictions_${horizonMonths}mo_${new Date().toISOString().split('T')[0]}.${format === 'pdf' ? 'pdf' : 'xlsx'}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
    } catch (error) {
      console.error('Export error:', error);
      alert('Export failed. Please try again.');
    }
    setExporting(false);
  };

  const fetchPredictions = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/predictions/trends?horizon_months=${horizonMonths}`, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      setPredictions(data);
    } catch (error) {
      console.error('Error fetching predictions:', error);
      // Set fallback data with mock metrics for demo
      setPredictions({
        generated_at: new Date().toISOString(),
        horizon_months: horizonMonths,
        confidence_level: 0.85,
        metrics: {
          emissions: {
            metric_name: "Total Emissions",
            unit: "tCO2e",
            current_value: 70500,
            forecast: [
              { month: 1, predicted_value: 69800, lower_bound: 67500, upper_bound: 72100 },
              { month: 3, predicted_value: 68400, lower_bound: 65000, upper_bound: 71800 },
              { month: 6, predicted_value: 66300, lower_bound: 61500, upper_bound: 71100 },
              { month: 12, predicted_value: 62100, lower_bound: 54500, upper_bound: 69700 }
            ],
            trend_direction: "decreasing",
            predicted_change_percent: -11.9,
            risk_factors: ["Supply chain disruptions", "Weather variability"]
          },
          compliance_score: {
            metric_name: "Compliance Score",
            unit: "%",
            current_value: 78,
            forecast: [
              { month: 1, predicted_value: 79, lower_bound: 77, upper_bound: 81 },
              { month: 3, predicted_value: 83, lower_bound: 79, upper_bound: 87 },
              { month: 6, predicted_value: 88, lower_bound: 82, upper_bound: 94 },
              { month: 12, predicted_value: 95, lower_bound: 87, upper_bound: 99 }
            ],
            trend_direction: "increasing",
            predicted_change_percent: 21.8,
            risk_factors: ["New regulatory requirements", "Data availability"]
          },
          esg_score: {
            metric_name: "ESG Score",
            unit: "points",
            current_value: 62.8,
            forecast: [
              { month: 1, predicted_value: 64.0, lower_bound: 62.5, upper_bound: 65.5 },
              { month: 3, predicted_value: 66.5, lower_bound: 64.0, upper_bound: 69.0 },
              { month: 6, predicted_value: 70.0, lower_bound: 66.0, upper_bound: 74.0 },
              { month: 12, predicted_value: 77.0, lower_bound: 70.0, upper_bound: 84.0 }
            ],
            trend_direction: "increasing",
            predicted_change_percent: 22.6,
            risk_factors: ["Market conditions", "Stakeholder expectations"]
          },
          renewable_energy: {
            metric_name: "Renewable Energy Usage",
            unit: "%",
            current_value: 42,
            forecast: [
              { month: 1, predicted_value: 44, lower_bound: 42, upper_bound: 46 },
              { month: 3, predicted_value: 48, lower_bound: 44, upper_bound: 52 },
              { month: 6, predicted_value: 55, lower_bound: 49, upper_bound: 61 },
              { month: 12, predicted_value: 68, lower_bound: 58, upper_bound: 78 }
            ],
            trend_direction: "increasing",
            predicted_change_percent: 61.9,
            risk_factors: ["Infrastructure availability", "Cost fluctuations"]
          }
        },
        risk_assessment: { 
          overall_risk_level: 'medium', 
          key_risks: [
            { risk: "Regulatory Compliance Gap", probability: 0.35, impact: "high", mitigation: "Accelerate data collection" },
            { risk: "Supply Chain Disruption", probability: 0.25, impact: "medium", mitigation: "Diversify supplier base" }
          ] 
        },
        recommendations: [
          { priority: 1, action: "Focus on Scope 3 Data Collection", impact: "High", timeline: "Q1 2025", expected_improvement: "+8% compliance" },
          { priority: 2, action: "Accelerate Renewable Energy Transition", impact: "High", timeline: "Q2 2025", expected_improvement: "-15% emissions" }
        ]
      });
    }
    setLoading(false);
  };

  const fetchScenarios = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/predictions/scenario-analysis`, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      setScenarios(data.scenarios);
    } catch (error) {
      console.error('Error fetching scenarios:', error);
      // Set fallback scenarios for demo
      setScenarios({
        base_case: {
          name: "Base Case",
          description: "Current trajectory with planned initiatives",
          probability: 0.50,
          outcomes: { esg_score_2025: 77, emissions_reduction: -12, compliance_score: 95 }
        },
        optimistic: {
          name: "Optimistic",
          description: "Accelerated initiatives with favorable conditions",
          probability: 0.25,
          outcomes: { esg_score_2025: 85, emissions_reduction: -20, compliance_score: 98 }
        },
        conservative: {
          name: "Conservative",
          description: "Slower progress with headwinds",
          probability: 0.25,
          outcomes: { esg_score_2025: 68, emissions_reduction: -5, compliance_score: 88 }
        }
      });
    }
  };

  // Fetch data on mount and when horizonMonths changes
  useEffect(() => {
    fetchPredictions();
    fetchScenarios();
  }, [horizonMonths]); // eslint-disable-line

  const getMetricConfig = (key) => {
    const configs = {
      emissions: { color: 'emerald', icon: TrendingDown, label: 'Emissions' },
      compliance_score: { color: 'blue', icon: Target, label: 'Compliance' },
      esg_score: { color: 'purple', icon: BarChart3, label: 'ESG Score' },
      renewable_energy: { color: 'amber', icon: Zap, label: 'Renewable Energy' }
    };
    return configs[key] || { color: 'slate', icon: BarChart3, label: key };
  };

  const renderForecastChart = (metric) => {
    if (!metric?.forecast) return null;
    
    const maxValue = Math.max(...metric.forecast.map(f => f.upper_bound));
    const minValue = Math.min(...metric.forecast.map(f => f.lower_bound));
    const range = maxValue - minValue;
    
    return (
      <div className="relative h-48 mt-4">
        {/* Grid lines */}
        <div className="absolute inset-0 flex flex-col justify-between">
          {[0, 1, 2, 3, 4].map(i => (
            <div key={i} className="border-t border-slate-100 w-full" />
          ))}
        </div>
        
        {/* Forecast visualization */}
        <div className="absolute inset-0 flex items-end justify-around px-4">
          {metric.forecast.map((point, idx) => {
            const heightPercent = ((point.predicted_value - minValue) / range) * 100;
            const upperHeight = ((point.upper_bound - minValue) / range) * 100;
            const lowerHeight = ((point.lower_bound - minValue) / range) * 100;
            
            return (
              <div key={idx} className="flex flex-col items-center w-16">
                <div className="relative w-full flex flex-col items-center" style={{ height: '180px' }}>
                  {/* Confidence interval bar */}
                  <div 
                    className="absolute w-3 bg-emerald-100 rounded-full"
                    style={{
                      bottom: `${lowerHeight}%`,
                      height: `${upperHeight - lowerHeight}%`
                    }}
                  />
                  {/* Predicted value dot */}
                  <div 
                    className="absolute w-4 h-4 bg-emerald-600 rounded-full border-2 border-white shadow-md z-10"
                    style={{
                      bottom: `calc(${heightPercent}% - 8px)`
                    }}
                  />
                </div>
                <div className="text-xs text-slate-500 mt-2">M{point.month}</div>
              </div>
            );
          })}
        </div>
        
        {/* Y-axis labels */}
        <div className="absolute left-0 inset-y-0 flex flex-col justify-between text-xs text-slate-400 -ml-8">
          <span>{Math.round(maxValue).toLocaleString()}</span>
          <span>{Math.round(minValue).toLocaleString()}</span>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <RefreshCw size={32} className="text-emerald-600 animate-spin" />
      </div>
    );
  }

  const metrics = predictions?.metrics || {};
  const currentMetric = metrics[selectedMetric];

  return (
    <div className="space-y-6" data-testid="predictive-analytics">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button 
            onClick={() => navigate(-1)} 
            className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
            data-testid="back-button"
          >
            <ArrowLeft size={20} className="text-slate-600" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Predictive Analytics</h1>
            <p className="text-sm text-slate-500">AI-powered ESG trend forecasting and risk analysis</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={horizonMonths}
            onChange={(e) => setHorizonMonths(parseInt(e.target.value))}
            className="px-4 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
            data-testid="horizon-select"
          >
            <option value={6}>6 Months</option>
            <option value={12}>12 Months</option>
            <option value={24}>24 Months</option>
          </select>
          
          {/* Export Dropdown */}
          <div className="relative group">
            <button
              disabled={exporting}
              className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 rounded-lg text-sm hover:bg-slate-50 transition-colors disabled:opacity-50"
              data-testid="export-btn"
            >
              <Download size={16} className={exporting ? 'animate-bounce' : ''} />
              {exporting ? 'Exporting...' : 'Export'}
            </button>
            <div className="absolute right-0 mt-1 w-48 bg-white rounded-lg shadow-lg border border-slate-200 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50">
              <button
                onClick={() => handleExport('pdf')}
                disabled={exporting}
                className="flex items-center gap-3 w-full px-4 py-3 text-sm text-slate-700 hover:bg-slate-50 transition-colors rounded-t-lg"
                data-testid="export-pdf-btn"
              >
                <FileText size={16} className="text-red-500" />
                Export as PDF
              </button>
              <button
                onClick={() => handleExport('excel')}
                disabled={exporting}
                className="flex items-center gap-3 w-full px-4 py-3 text-sm text-slate-700 hover:bg-slate-50 transition-colors rounded-b-lg border-t border-slate-100"
                data-testid="export-excel-btn"
              >
                <FileSpreadsheet size={16} className="text-green-600" />
                Export as Excel
              </button>
            </div>
          </div>
          
          <button
            onClick={fetchPredictions}
            className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
            data-testid="refresh-btn"
          >
            <RefreshCw size={16} />
            Refresh
          </button>
        </div>
      </div>

      {/* Confidence Level Banner */}
      <div className="bg-gradient-to-r from-emerald-50 to-blue-50 border border-emerald-200 rounded-xl p-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-emerald-100 rounded-lg">
            <Info size={20} className="text-emerald-600" />
          </div>
          <div>
            <p className="text-sm font-medium text-slate-900">Model Confidence: {((predictions?.confidence_level || 0.85) * 100).toFixed(0)}%</p>
            <p className="text-xs text-slate-500">Based on historical data patterns and industry benchmarks</p>
          </div>
        </div>
        <div className="text-xs text-slate-500">
          Last updated: {new Date(predictions?.generated_at || Date.now()).toLocaleString()}
        </div>
      </div>

      {/* Metric Selector */}
      <div className="grid grid-cols-4 gap-4">
        {Object.entries(metrics).map(([key, metric]) => {
          const config = getMetricConfig(key);
          const Icon = config.icon;
          const isSelected = selectedMetric === key;
          const isIncreasing = metric.trend_direction === 'increasing';
          
          return (
            <button
              key={key}
              onClick={() => setSelectedMetric(key)}
              className={`p-4 rounded-xl border transition-all text-left ${
                isSelected 
                  ? `bg-${config.color}-50 border-${config.color}-300 ring-2 ring-${config.color}-500`
                  : 'bg-white border-slate-200 hover:border-slate-300'
              }`}
              data-testid={`metric-${key}`}
            >
              <div className="flex items-start justify-between mb-2">
                <Icon size={20} className={`text-${config.color}-600`} />
                {isIncreasing ? (
                  <ArrowUpRight size={16} className="text-emerald-600" />
                ) : (
                  <ArrowDownRight size={16} className="text-red-600" />
                )}
              </div>
              <div className="text-xl font-bold text-slate-900">
                {metric.current_value?.toLocaleString()}{metric.unit === '%' && '%'}
              </div>
              <div className="text-xs text-slate-500">{metric.metric_name}</div>
              <div className={`text-xs mt-1 ${
                (key === 'emissions' ? !isIncreasing : isIncreasing) 
                  ? 'text-emerald-600' : 'text-red-600'
              }`}>
                {metric.predicted_change_percent > 0 ? '+' : ''}{metric.predicted_change_percent?.toFixed(1)}% predicted
              </div>
            </button>
          );
        })}
      </div>

      {/* Main Forecast Panel */}
      {currentMetric && (
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <div className="flex items-start justify-between mb-6">
            <div>
              <h2 className="text-lg font-semibold text-slate-900">{currentMetric.metric_name} Forecast</h2>
              <p className="text-sm text-slate-500">
                {horizonMonths}-month projection with confidence intervals
              </p>
            </div>
            <div className={`px-3 py-1.5 rounded-lg ${
              currentMetric.trend_direction === 'increasing' 
                ? 'bg-emerald-100 text-emerald-700' 
                : 'bg-red-100 text-red-700'
            }`}>
              <div className="flex items-center gap-1 text-sm font-medium">
                {currentMetric.trend_direction === 'increasing' ? (
                  <TrendingUp size={16} />
                ) : (
                  <TrendingDown size={16} />
                )}
                {currentMetric.trend_direction.charAt(0).toUpperCase() + currentMetric.trend_direction.slice(1)}
              </div>
            </div>
          </div>

          {/* Forecast Chart */}
          {renderForecastChart(currentMetric)}

          {/* Forecast Details */}
          <div className="mt-6 grid grid-cols-3 gap-4">
            <div className="p-4 bg-slate-50 rounded-lg">
              <div className="text-xs text-slate-500 mb-1">Current Value</div>
              <div className="text-xl font-bold text-slate-900">
                {currentMetric.current_value?.toLocaleString()} {currentMetric.unit}
              </div>
            </div>
            <div className="p-4 bg-emerald-50 rounded-lg">
              <div className="text-xs text-slate-500 mb-1">12-Month Forecast</div>
              <div className="text-xl font-bold text-emerald-700">
                {currentMetric.forecast?.[currentMetric.forecast.length - 1]?.predicted_value?.toLocaleString()} {currentMetric.unit}
              </div>
            </div>
            <div className="p-4 bg-blue-50 rounded-lg">
              <div className="text-xs text-slate-500 mb-1">Predicted Change</div>
              <div className={`text-xl font-bold ${
                (selectedMetric === 'emissions' ? currentMetric.predicted_change_percent < 0 : currentMetric.predicted_change_percent > 0)
                  ? 'text-emerald-700' : 'text-red-700'
              }`}>
                {currentMetric.predicted_change_percent > 0 ? '+' : ''}{currentMetric.predicted_change_percent?.toFixed(1)}%
              </div>
            </div>
          </div>

          {/* Risk Factors */}
          {currentMetric.risk_factors && (
            <div className="mt-6 pt-6 border-t border-slate-100">
              <h3 className="text-sm font-semibold text-slate-700 mb-3">Key Risk Factors</h3>
              <div className="flex flex-wrap gap-2">
                {currentMetric.risk_factors.map((factor, idx) => (
                  <span
                    key={idx}
                    className="px-3 py-1.5 bg-amber-50 text-amber-700 text-sm rounded-full"
                  >
                    <AlertTriangle size={12} className="inline mr-1" />
                    {factor}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Scenario Analysis */}
      {scenarios && (
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h2 className="text-lg font-semibold text-slate-900 mb-4">Scenario Analysis</h2>
          <div className="grid grid-cols-3 gap-4">
            {Object.entries(scenarios).map(([key, scenario]) => (
              <div
                key={key}
                className={`p-4 rounded-xl border ${
                  key === 'base_case' ? 'border-blue-200 bg-blue-50' :
                  key === 'optimistic' ? 'border-emerald-200 bg-emerald-50' :
                  'border-amber-200 bg-amber-50'
                }`}
                data-testid={`scenario-${key}`}
              >
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold text-slate-900">{scenario.name}</h3>
                  <span className="text-xs text-slate-500">
                    {(scenario.probability * 100).toFixed(0)}% likely
                  </span>
                </div>
                <p className="text-xs text-slate-600 mb-4">{scenario.description}</p>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">ESG Score (2025)</span>
                    <span className="font-medium">{scenario.outcomes?.esg_score_2025}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Emissions Change</span>
                    <span className={`font-medium ${scenario.outcomes?.emissions_reduction < 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                      {scenario.outcomes?.emissions_reduction}%
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Compliance</span>
                    <span className="font-medium">{scenario.outcomes?.compliance_score}%</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Risk Assessment */}
      {predictions?.risk_assessment && (
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-slate-900">Risk Assessment</h2>
            <span className={`px-3 py-1.5 rounded-lg text-sm font-medium ${
              predictions.risk_assessment.overall_risk_level === 'low' ? 'bg-emerald-100 text-emerald-700' :
              predictions.risk_assessment.overall_risk_level === 'medium' ? 'bg-amber-100 text-amber-700' :
              'bg-red-100 text-red-700'
            }`}>
              {predictions.risk_assessment.overall_risk_level.toUpperCase()} RISK
            </span>
          </div>
          
          <div className="space-y-3">
            {predictions.risk_assessment.key_risks?.map((risk, idx) => (
              <div
                key={idx}
                className="p-4 bg-slate-50 rounded-lg"
              >
                <div className="flex items-start justify-between">
                  <div>
                    <h4 className="font-medium text-slate-900">{risk.risk}</h4>
                    <p className="text-sm text-slate-500 mt-1">{risk.mitigation}</p>
                  </div>
                  <div className="text-right">
                    <div className="text-xs text-slate-500">Probability</div>
                    <div className="font-semibold text-slate-900">{(risk.probability * 100).toFixed(0)}%</div>
                  </div>
                </div>
                <div className="mt-2 flex items-center gap-2">
                  <span className={`px-2 py-0.5 text-xs rounded-full ${
                    risk.impact === 'high' ? 'bg-red-100 text-red-700' :
                    risk.impact === 'medium' ? 'bg-amber-100 text-amber-700' :
                    'bg-blue-100 text-blue-700'
                  }`}>
                    {risk.impact} impact
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recommendations */}
      {predictions?.recommendations && (
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h2 className="text-lg font-semibold text-slate-900 mb-4">AI Recommendations</h2>
          <div className="space-y-3">
            {predictions.recommendations.map((rec, idx) => (
              <div
                key={idx}
                className="flex items-start gap-4 p-4 bg-emerald-50 border border-emerald-200 rounded-lg"
              >
                <div className="flex-shrink-0 w-8 h-8 bg-emerald-600 text-white rounded-full flex items-center justify-center font-bold text-sm">
                  {rec.priority}
                </div>
                <div className="flex-1">
                  <h4 className="font-medium text-slate-900">{rec.action}</h4>
                  <div className="mt-2 flex items-center gap-4 text-sm text-slate-600">
                    <span className="flex items-center gap-1">
                      <Calendar size={14} />
                      {rec.timeline}
                    </span>
                    <span className="flex items-center gap-1">
                      <Target size={14} />
                      {rec.expected_improvement}
                    </span>
                    <span className={`px-2 py-0.5 rounded-full text-xs ${
                      rec.impact === 'High' ? 'bg-emerald-100 text-emerald-700' :
                      'bg-blue-100 text-blue-700'
                    }`}>
                      {rec.impact} Impact
                    </span>
                  </div>
                </div>
                <ChevronRight size={20} className="text-slate-400" />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default PredictiveAnalytics;
